import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { config } from '../config';
import { User, ApiError, ApiResponse } from '../types';

// Authentication request/response types
export interface LoginRequest {
  email: string; // Email only - no username login
  password: string;
  inviteVariables?: Record<string, string>;
  /** Guest user ID when logging in as guest to transfer invite variables to real user */
  guestUserId?: string;
  /** Guest token for backend to validate guest when transferring invite variables */
  guestToken?: string;
}

export interface RegisterRequest {
  email: string;
  username: string;
  password: string;
}

export interface ResetPasswordRequest {
  email: string;
  newPassword: string;
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface AuthResponse {
  user: User;
  token: string; // JWT
  refreshToken: string;
}

export interface RefreshResponse {
  token: string; // New JWT
  refreshToken: string; // New refresh token
}

export interface ResetPasswordResponse {
  success: boolean;
  message: string;
}

export interface MeResponse {
  user: User;
}

class AuthService {
  private axiosInstance: AxiosInstance;
  private jwtToken: string | null = null;
  private refreshToken: string | null = null;
  private refreshPromise: Promise<string> | null = null;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: config.apiBaseUrl,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
    this.loadTokens();
  }

  private setupInterceptors(): void {
    // Request interceptor to add JWT token
    this.axiosInstance.interceptors.request.use(
      (config) => {
        if (this.jwtToken) {
          config.headers.Authorization = `Bearer ${this.jwtToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for token refresh
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        return response;
      },
      async (error: AxiosError<ApiResponse>) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
        
        // Don't try to refresh token for auth endpoints (login, register, refresh)
        const isAuthEndpoint = originalRequest?.url?.includes('/api/auth/');
        
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthEndpoint) {
          // Don't try refresh or logout for guests - they use guest tokens, not authService tokens
          const { default: useAuthStore } = await import('../stores/authStore');
          const { default: guestService } = await import('./guestService');
          const authState = useAuthStore.getState();
          const hasGuestData = !!guestService.getGuestData();
          if (authState.isGuest || hasGuestData) {
            return Promise.reject(this.transformError(error));
          }
          originalRequest._retry = true;
          
          try {
            const newToken = await this.refreshJwtToken();
            if (newToken && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.axiosInstance(originalRequest);
            }
          } catch (refreshError) {
            // Don't logout/redirect when user is a guest (e.g. guest login flow in adventure)
            const { default: useAuthStore } = await import('../stores/authStore');
            const { default: guestService } = await import('./guestService');
            const authState = useAuthStore.getState();
            const hasGuestData = !!guestService.getGuestData();
            if (!authState.isGuest && !hasGuestData) {
              this.handleAuthFailure();
            }
            return Promise.reject(refreshError);
          }
        }
        
        return Promise.reject(this.transformError(error));
      }
    );
  }

  private loadTokens(): void {
    // Load refresh token from localStorage (persists across sessions)
    this.refreshToken = localStorage.getItem('refreshToken');
    
    // JWT token is not persisted - it will be refreshed on app startup if needed
    this.jwtToken = null;
  }

  private handleAuthFailure(dispatchEvent: boolean = true): void {
    this.clearTokens();
    // Dispatch logout event for components to handle (unless suppressed)
    if (dispatchEvent) {
      window.dispatchEvent(new CustomEvent('auth:logout'));
    }
  }

  private transformError(error: AxiosError<ApiResponse>): ApiError {
    if (error.response?.data?.error) {
      return error.response.data.error;
    }

    if (error.response?.data?.message) {
      // Transform generic "Invalid credentials" to more helpful messages
      let message = error.response.data.message;
      if (message === 'Invalid credentials') {
        message = 'Invalid email or password. Please check your credentials and try again.';
      }
      
      return {
        code: error.response.data.code || 'API_ERROR',
        message: message,
        details: error.response.data
      };
    }

    if (error.code === 'ECONNABORTED') {
      return {
        code: 'TIMEOUT',
        message: 'Request timeout. Please try again.',
      };
    }

    if (!error.response) {
      return {
        code: 'NETWORK_ERROR',
        message: 'Network error. Please check your connection.',
      };
    }

    return {
      code: 'UNKNOWN_ERROR',
      message: error.message || 'An unexpected error occurred',
      details: error.response.data,
    };
  }

  // Token management methods
  private setTokens(jwtToken: string, refreshToken: string): void {
    console.log('[AuthService] setTokens: Storing tokens', {
      hasJwtToken: !!jwtToken,
      hasRefreshToken: !!refreshToken,
      refreshTokenLength: refreshToken?.length || 0,
    });
    this.jwtToken = jwtToken;
    this.refreshToken = refreshToken;
    
    // Store refresh token in localStorage (persists across sessions)
    localStorage.setItem('refreshToken', refreshToken);
    console.log('[AuthService] setTokens: Refresh token stored in localStorage, verified:', {
      stored: !!localStorage.getItem('refreshToken'),
      matches: localStorage.getItem('refreshToken') === refreshToken,
    });
    
    // JWT token is kept in memory only (cleared on page refresh)
    // This provides security while allowing automatic refresh
  }

  private clearTokens(): void {
    this.jwtToken = null;
    this.refreshToken = null;
    localStorage.removeItem('refreshToken');
  }

  private async refreshJwtToken(): Promise<string> {
    if (!this.refreshToken) {
      throw new Error('No refresh token available');
    }

    // Prevent multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    
    try {
      const newToken = await this.refreshPromise;
      return newToken;
    } finally {
      this.refreshPromise = null;
    }
  }

  /** True if the error is due to network/server unavailability (do not clear tokens). */
  private isTransientOrNetworkError(error: unknown): boolean {
    if (axios.isAxiosError(error)) {
      if (error.response == null) return true; // No response = network failure
      if (error.response.status >= 500) return true; // Server error, may be temporary
    }
    return false;
  }

  private async performTokenRefresh(): Promise<string> {
    try {
      const response = await axios.post<ApiResponse<RefreshResponse>>(
        `${config.apiBaseUrl}/api/auth/refresh`,
        { refreshToken: this.refreshToken }
      );

      if (!response.data.data) {
        throw new Error('Invalid refresh response');
      }

      const { token, refreshToken: newRefreshToken } = response.data.data;
      this.setTokens(token, newRefreshToken);
      
      return token;
    } catch (error) {
      // Only clear tokens on auth failure (e.g. 401), not on network/server errors
      if (!this.isTransientOrNetworkError(error)) {
        this.clearTokens();
      }
      throw error;
    }
  }

  // Public authentication methods
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response = await this.axiosInstance.post<ApiResponse<AuthResponse>>(
        '/api/auth/login',
        credentials
      );

      if (!response.data.data) {
        throw new Error('Invalid login response');
      }

      const { user, token, refreshToken } = response.data.data;
      this.setTokens(token, refreshToken);

      return { user, token, refreshToken };
    } catch (error) {
      // Don't clear tokens on login failure - this prevents triggering refresh token logic
      // Only clear tokens if we had them before (i.e., user was already logged in)
      if (this.jwtToken || this.refreshToken) {
        this.handleAuthFailure(false); // Suppress logout event during login failure
      }
      throw error;
    }
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    try {
      const response = await this.axiosInstance.post<ApiResponse<AuthResponse>>(
        '/api/auth/register',
        userData
      );

      if (!response.data.data) {
        throw new Error('Invalid registration response');
      }

      const { user, token, refreshToken } = response.data.data;
      this.setTokens(token, refreshToken);

      return { user, token, refreshToken };
    } catch (error) {
      // Don't clear tokens on registration failure - this prevents triggering refresh token logic
      // Only clear tokens if we had them before (i.e., user was already logged in)
      if (this.jwtToken || this.refreshToken) {
        this.handleAuthFailure(false); // Suppress logout event during registration failure
      }
      throw error;
    }
  }

  /**
   * Exchange guest token for user tokens after guest migration (e.g., after async login)
   * @param guestToken - The guest token to exchange (required since it's not in authService)
   */
  async exchangeGuestToken(guestToken: string): Promise<AuthResponse> {
    try {
      // Create a one-time request with the guest token in the Authorization header
      const response = await axios.post<ApiResponse<AuthResponse>>(
        `${config.apiBaseUrl}/api/auth/exchange-guest-token`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${guestToken}`,
          },
          timeout: 10000,
        }
      );

      if (!response.data.data) {
        throw new Error('Invalid exchange response');
      }

      const { user, token, refreshToken } = response.data.data;
      this.setTokens(token, refreshToken);

      return { user, token, refreshToken };
    } catch (error) {
      // Transform axios errors to ApiError so callers (e.g. gameStore) see code/message
      // and can treat "Guest user has not been migrated yet" (400) as expected
      if (axios.isAxiosError(error)) {
        throw this.transformError(error as AxiosError<ApiResponse>);
      }
      throw error;
    }
  }

  /**
   * Delete the authenticated user's account
   */
  async deleteAccount(): Promise<{ success: boolean; message: string }> {
    const response = await this.axiosInstance.delete<ApiResponse<{ success: boolean; message: string }>>(
      '/api/auth/account'
    );

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete account');
    }

    return response.data.data || { success: true, message: 'Account deleted successfully' };
  }

  async logout(): Promise<void> {
    try {
      // Call logout endpoint to invalidate tokens on server
      if (this.jwtToken) {
        await this.axiosInstance.post('/api/auth/logout');
      }
    } catch (error) {
      // Continue with logout even if server call fails
      console.warn('Logout server call failed:', error);
    } finally {
      this.clearTokens();
    }
  }

  async resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    const response = await this.axiosInstance.post<ApiResponse<ResetPasswordResponse>>(
      '/api/auth/reset-password',
      data
    );

    if (!response.data.data) {
      throw new Error('Invalid reset password response');
    }

    return response.data.data;
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.axiosInstance.get<ApiResponse<MeResponse>>('/api/auth/me');

    if (!response.data.data?.user) {
      throw new Error('User not found');
    }

    return response.data.data.user;
  }

  // Utility methods
  isAuthenticated(): boolean {
    return !!this.jwtToken;
  }

  getCurrentToken(): string | null {
    return this.jwtToken;
  }

  hasRefreshToken(): boolean {
    // Also check localStorage in case the in-memory value is stale
    const storedRefreshToken = localStorage.getItem('refreshToken');
    const hasToken = !!(this.refreshToken || storedRefreshToken);
    console.log('[AuthService] hasRefreshToken:', {
      inMemory: !!this.refreshToken,
      inStorage: !!storedRefreshToken,
      result: hasToken,
    });
    return hasToken;
  }

  // Auto-refresh logic - call this periodically to refresh JWT before expiration
  async ensureValidToken(): Promise<boolean> {
    if (!this.jwtToken) {
      // No JWT token, try to refresh
      if (this.refreshToken) {
        try {
          await this.refreshJwtToken();
          return true;
        } catch (error) {
          return false;
        }
      }
      return false;
    }

    // Check if JWT is close to expiration (refresh 5 minutes before)
    try {
      const tokenParts = this.jwtToken.split('.');
      if (tokenParts.length !== 3) {
        throw new Error('Invalid JWT format');
      }
      
      const payload = JSON.parse(atob(tokenParts[1]!));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiration = expirationTime - currentTime;
      
      // If token expires in less than 5 minutes, refresh it
      if (timeUntilExpiration < 5 * 60 * 1000) {
        if (this.refreshToken) {
          await this.refreshJwtToken();
          return true;
        }
        return false;
      }
      
      return true;
    } catch (error) {
      // Invalid JWT format, try to refresh
      if (this.refreshToken) {
        try {
          await this.refreshJwtToken();
          return true;
        } catch (refreshError) {
          return false;
        }
      }
      return false;
    }
  }

  // Health check for authentication status
  async healthCheck(): Promise<boolean> {
    try {
      await this.getCurrentUser();
      return true;
    } catch (error) {
      return false;
    }
  }
}

// Create singleton instance
const authService = new AuthService();

export default authService;
